import { useState, useEffect } from "react";
import { ModalShell } from "./ui/modal";
import { Button } from "./ui/button";
import { Check, Zap, Loader2 } from "lucide-react";
import { getPlans, createCheckoutSession } from "../api/subscriptions";
import type { SubscriptionPlan } from "../types";
import { useLanguage } from "../hooks/useLanguage";

interface PlanLimitModalProps {
    open: boolean;
    onClose: () => void;
    currentPlan: string;
    pageCount: number;
    limit: number;
}

export function PlanLimitModal({ open, onClose, currentPlan, pageCount, limit }: PlanLimitModalProps) {
    const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const { t } = useLanguage();

    useEffect(() => {
        if (open) {
            getPlans().then((plansData) => {
                console.log('PlanLimitModal - Loaded plans:', plansData);
                setPlans(plansData);
            }).catch(console.error);
        }
    }, [open]);

    const handleCheckout = async (plan: SubscriptionPlan) => {
        try {
            setLoadingPlan(plan.slug);
            // Default to monthly billing for now as per modal display
            const { url } = await createCheckoutSession(plan.id, "monthly");
            window.location.href = url;
        } catch (error) {
            console.error("Checkout error:", error);
            setLoadingPlan(null);
            alert(t.common.error);
        }
    };

    const formatPrice = (cents: number) => {
        return `$${(cents / 100).toFixed(2)}`;
    };

    return (
        <ModalShell open={open} onClose={onClose} contentClassName="!max-w-3xl">
            <div className="bg-white rounded-3xl p-8 shadow-xl w-full">
                <div className="text-center pb-6">
                    <div className="mx-auto w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                        <Zap className="w-6 h-6 text-amber-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900">
                        {t.modals.planLimit.title}
                    </h2>
                    <p className="text-lg text-slate-600 pt-2">
                        {t.modals.planLimit.description
                            .replace("{limit}", limit.toString())
                            .replace("{pages}", limit === 1 ? t.modals.planLimit.pagesSingular : t.modals.planLimit.pagesPlural)
                            .replace("{plan}", currentPlan === 'free' ? t.dashboard.planFree : currentPlan)
                        }
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6 py-4">
                    {plans.filter(plan => plan.slug !== 'free').map((plan) => (
                        <div
                            key={plan.slug}
                            className="relative rounded-2xl border-2 border-slate-100 p-6 hover:border-[#0DAFFD]/30 hover:shadow-lg transition-all"
                        >
                            <h3 className="text-xl font-bold text-slate-900">{plan.name}</h3>
                            <div className="mt-2 flex items-baseline gap-1">
                                <span className="text-3xl font-bold bg-gradient-to-r from-[#0DAFFD] to-[#0FD8FE] text-transparent bg-clip-text">
                                    {formatPrice(plan.price_monthly_cents || 0)}{t.pricing.perMonth}
                                </span>
                            </div>

                            <ul className="mt-6 space-y-3">
                                {(plan.slug === 'pro'
                                    ? t.pricing.plans.pro.features
                                    : plan.slug === 'premium'
                                    ? t.pricing.plans.premium.features
                                    : plan.features && Array.isArray(plan.features) ? plan.features : []
                                ).slice(0, 6).map((feature, index) => (
                                    <li key={index} className="flex items-center gap-2 text-sm text-slate-600">
                                        <Check className="w-4 h-4 text-emerald-500" />
                                        {feature}
                                    </li>
                                ))}
                            </ul>

                            <Button
                                className="w-full mt-8 bg-gradient-to-r from-[#0DAFFD] to-[#0FD8FE] text-white font-semibold shadow-lg shadow-brand-primary/20"
                                onClick={() => handleCheckout(plan)}
                                disabled={!!loadingPlan}
                            >
                                {loadingPlan === plan.slug ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {t.modals.planLimit.processing}
                                    </>
                                ) : (
                                    t.modals.planLimit.selectPlan.replace("{name}", plan.name)
                                )}
                            </Button>
                        </div>
                    ))}
                </div>

                <div className="mt-6 text-center">
                    <Button
                        variant="ghost"
                        onClick={onClose}
                        disabled={!!loadingPlan}
                    >
                        {t.modals.planLimit.cancel}
                    </Button>
                </div>
            </div>
        </ModalShell>
    );
}
